import { App, message as antdMessage } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import React, { useEffect } from 'react';
import { isRequestHaltedError } from './requestHalt';

let messageApi: MessageInstance | null = null;

export const setMessageInstance = (instance: MessageInstance) => {
  messageApi = instance;
};

/**
 * Извлекает сообщение об ошибке из различных форматов ответа
 * Поддерживает новый формат с errorCode и message
 * Поддерживает RTK Query error format: error.data.message
 * Поддерживает простые строки (для локальной валидации)
 */
export const getErrorMessage = (error: any): string => {
  // If error is already a string, return it directly
  if (typeof error === 'string') {
    return error;
  }
  
  // RTK Query error format: error.data.message (FetchBaseQueryError)
  if (error?.data?.message) {
    return error.data.message;
  }
  
  // Axios error format: error.response.data.message
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Fallback на старые форматы
  if (error?.message) {
    return error.message;
  }
  
  return 'Ошибка';
};

/**
 * Извлекает код ошибки если он есть
 * Поддерживает RTK Query и Axios форматы
 */
export const getErrorCode = (error: any): string | null => {
  // RTK Query format
  if (error?.data?.errorCode) {
    return error.data.errorCode;
  }
  // Axios format
  if (error?.response?.data?.errorCode) {
    return error.response.data.errorCode;
  }
  return null;
};

/**
 * Извлекает дополнительные мета-данные ошибки
 * Поддерживает RTK Query и Axios форматы
 */
export const getErrorMeta = (error: any): any => {
  // RTK Query format
  if (error?.data?.meta) {
    return error.data.meta;
  }
  // Axios format
  if (error?.response?.data?.meta) {
    return error.response.data.meta;
  }
  return null;
};

export const toastError = (error: any): void => {
  if (isRequestHaltedError(error)) {
    return;
  }

  const errorCode = getErrorCode(error);
  const meta = getErrorMeta(error);
  void errorCode;
  void meta;

  const message = getErrorMessage(error);

  if (messageApi) {
    messageApi.error(message);
  } else {
    antdMessage.error(message);
  }
};

export const toastSuccess = (text: string): void => {
  if (messageApi) {
    messageApi.success(text);
  } else {
    antdMessage.success(text);
  }
};

export const useToast = () => {
  const { message } = App.useApp();
  useEffect(() => {
    setMessageInstance(message);
  }, [message]);

  return {
    toastError: (err: any) => message.error(getErrorMessage(err)),
    toastSuccess: (text: string) => message.success(text),
  };
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  useToast();
  return <>{children}</>;
};
